import React, { useMemo } from 'react';
import type { GoodsReview } from 'shared/src/api/goodsService/getGoodsReviews';
import { useGoodsReviewsMetadata } from '../../../queries/useGoodsReviewsMetadata';
import { isNotEmpty, isNotNil } from 'shared/src/utils';
import { Flex, Typography } from '@banhala/ui-kit';
import { useGoodsReviewsAvailableInfo } from '../../../../../../../queries/useGoodsReviewsAvailableInfo';
import {
  ColorRate,
  SizeRate,
} from '../../../../../../../../../../../../shared/src/constant/domain/review/RateText';

const createBodyLabel = ({
  isNeedSizeInfo,
  extraBodyFieldType,
  height,
  weight,
  top,
  bottom,
  shoes,
  seperator = ' · ',
}: {
  isNeedSizeInfo: boolean;
  extraBodyFieldType: number | null;
  height: number | null;
  weight: number | null;
  top: number | null;
  bottom: number | null;
  shoes: number | null;
  seperator?: string;
}) => {
  const items: string[] = [];

  if (isNeedSizeInfo) {
    if (height) {
      items.push(`${height}cm`);
    }
    if (weight) {
      items.push(`${weight}kg`);
    }
  }

  // 상의
  if (extraBodyFieldType === 1 && top) {
    switch (top) {
      case 1:
        items.push('44 사이즈');
        break;
      case 2:
        items.push('44반 사이즈');
        break;
      case 3:
        items.push('55 사이즈');
        break;
      case 4:
        items.push('55반 사이즈');
        break;
      case 5:
        items.push('66 사이즈');
        break;
      case 6:
        items.push('66반 사이즈');
        break;
      case 7:
        items.push('77 사이즈');
        break;
      case 8:
        items.push('77반 사이즈');
        break;
      case 9:
        items.push('88 사이즈');
        break;
      case 10:
        items.push('88반 사이즈');
        break;
      case 11:
        items.push('99 사이즈');
        break;
      default:
        break;
    }
  }

  // 하의
  if (extraBodyFieldType === 2 && bottom) {
    items.push(`${bottom} 사이즈`);
  }

  // 신발
  if (extraBodyFieldType === 3 && shoes) {
    items.push(`${shoes} mm`);
  }

  return items.join(seperator);
};

const RowItem: React.FC<{ title: string; label: string }> = props => {
  return (
    <Flex.Row gap={12}>
      <Flex width={42}>
        <Typography type={'subtitle2'} color={'content_secondary'}>
          {props.title}
        </Typography>
      </Flex>
      <Typography type={'subtitle2'} color={'content_secondary'}>
        {props.label}
      </Typography>
    </Flex.Row>
  );
};

export const ReviewInfo: React.FC<{ review: GoodsReview }> = props => {
  const { data: reviewsMetadata } = useGoodsReviewsMetadata();
  const { data: reviewsAvailable } = useGoodsReviewsAvailableInfo();
  const bodyLabel = useMemo(() => {
    return createBodyLabel({
      isNeedSizeInfo: reviewsMetadata.need_size_info,
      extraBodyFieldType: props.review.require_extra_body_field,
      height: props.review.height,
      weight: props.review.weight,
      top: props.review.top,
      bottom: props.review.bottom,
      shoes: props.review.shoes,
    });
  }, [
    props.review.bottom,
    props.review.height,
    props.review.require_extra_body_field,
    props.review.shoes,
    props.review.top,
    props.review.weight,
    reviewsMetadata.need_size_info,
  ]);

  const skinLabel = useMemo(() => {
    const label = [];

    if (isNotNil(props.review.skin_tone)) {
      label.push(props.review.skin_tone);
    }

    if (isNotNil(props.review.skin_type)) {
      label.push(props.review.skin_type);
    }

    props.review.skin_problems.forEach(problem => {
      label.push(problem);
    });

    return label.join(' · ');
  }, [
    props.review.skin_problems,
    props.review.skin_tone,
    props.review.skin_type,
  ]);

  return (
    <Flex gap={8} padding={{ bottom: 16, horizontal: 16 }}>
      {props.review.goods_option.length !== 0 && (
        <RowItem title={'옵션'} label={props.review.goods_option.join(' · ')} />
      )}
      {isNotEmpty(bodyLabel) && <RowItem title={'체형'} label={bodyLabel} />}
      {isNotEmpty(skinLabel) && reviewsAvailable.is_available_skin_info && (
        <RowItem title={'피부'} label={skinLabel} />
      )}

      {isNotNil(props.review.size_rate) && props.review.size_rate !== 0 && (
        <RowItem
          title={'사이즈'}
          label={SizeRate[props.review.size_rate] ?? ''}
        />
      )}
      {isNotNil(props.review.color_rate) && props.review.color_rate !== 0 && (
        <RowItem
          title={'색상'}
          label={ColorRate[props.review.color_rate] ?? ''}
        />
      )}
    </Flex>
  );
};
